/**
 * 热点「打卡成功第三方回调」——图片打点（GET + query + new Image().src）
 *
 * 背景：部分第三方接口未配置 CORS，浏览器内 XHR/fetch 无法跨域读响应甚至发不出去；
 * 用图片加载方式发起 GET，可绕开对 JSON 响应的 CORS 限制（前端不读 body，仅触达对方）。
 *
 * 该方式副作用多（见历史讨论：无法读返回、URL 带参暴露、onerror 不可靠等），因此仅对白名单活动开启；
 * 其它活动一律在 activity-multi 中走 uni.request 的常规 GET/POST。
 */

/** 仅这些 activityCode（与路由 / 活动详情中的编码一致）允许走图片打点回调 */
export const ACTIVITY_CODES_USE_HOTSPOT_CALLBACK_BEACON = new Set(['2a6db7d56c764fa3'])

/**
 * 是否对当前活动使用「图片打点」回调（仍要求回调 URL 为外部 http(s)，见下方 isExternal）
 */
export function shouldUseHotspotCallbackBeacon(activityCode) {
  if (activityCode == null || activityCode === '') return false
  return ACTIVITY_CODES_USE_HOTSPOT_CALLBACK_BEACON.has(String(activityCode).trim())
}

/**
 * 是否为「外部站点」回调地址：此类在浏览器里易被 CORS 拦截，白名单活动才改用打点。
 * 相对路径、*.wvxvb.cn、localhost、常见内网段视为自有/联调环境，不走打点逻辑。
 */
export function isExternalHttpSuccessCallbackUrl(callbackUrl) {
  const s = String(callbackUrl || '').trim()
  if (!s || !/^https?:\/\//i.test(s)) return false
  try {
    const host = new URL(s).hostname.toLowerCase()
    if (host === 'localhost' || host === '127.0.0.1') return false
    if (/^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host)) return false
    if (host === 'wvxvb.cn' || host.endsWith('.wvxvb.cn')) return false
    return true
  } catch (e) {
    return false
  }
}

/**
 * 使用 GET query + 时间戳防缓存，通过 Image 触发请求（不解析响应体）。
 *
 * @param {string} callbackUrl 配置中的 successCallbackUrl（可已含 ?token= 等）
 * @param {Record<string, unknown>} payload 与页面 buildHotspotCallbackPayload 一致
 * @param {{ retainImg?: (img: HTMLImageElement) => void, timeout?: number }} [options]
 * @returns {string|undefined} 最终请求 URL（便于调试日志，可选）
 */
export function fireHotspotCallbackBeaconGet(callbackUrl, payload, options = {}) {
  const p = payload && typeof payload === 'object' ? payload : {}
  const query = Object.keys(p)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(p[key] == null ? '' : p[key])}`)
    .join('&')
  const base = String(callbackUrl || '').trim()
  const baseJoined = base.includes('?') ? `${base}&${query}` : `${base}?${query}`
  const url = `${baseJoined}&_t=${Date.now()}`

  if (typeof Image !== 'undefined') {
    const img = new Image()
    if (typeof options.retainImg === 'function') {
      options.retainImg(img)
    }
    img.src = url
    return url
  }

  if (typeof uni !== 'undefined' && typeof uni.request === 'function') {
    uni.request({ url, method: 'GET', timeout: options.timeout || 8000 })
  }
  return url
}
